import { useContext, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Frame } from '../../components/Frame'
import { useI18n } from '../../provider/i18n'
import Profiles from './sections/Profiles'
import Consents from './sections/Consents'
import Consultations from './sections/Consultations'
import Discounts from './sections/Discounts'
import Summary from './sections/Summary'
import Tests from './sections/Tests'
import Trainings from './sections/Trainings'
import ShippingTests from './sections/ShippingTests'
import MaterialPreparation from './sections/MaterialPreparation'
import { FormProvider, useForm } from 'react-hook-form'
import SelectSlotModal from '../../components/SelectSlotModal'
import SelectCollectionSlotModal from '../../components/SelectCollectionSlotModal'
import { MissingGlobalConsentsModal } from '../../components/MissingGlobalConsentsModal'
import moment from 'moment'
import useBasketData from '../../hooks/useBasketData'
import { MdDoDisturb } from 'react-icons/md'
import { Red800 } from '../../lib/colors'
import { AuthContext } from '../../provider/auth'
import { useAtomValue } from 'jotai'
import { isShowShippingTestsEnabledAtom } from '../../store/featureFlags.atoms'
import { addWarning } from '../../lib/messages'

export type BasketForm = {
  collectionDate: string
  clientNotes: string
  consent1: boolean
  consent3: boolean
  consent4: boolean
  shippingConsent1: boolean
  shippingConsent2: boolean
  shippingPhone: string
  shippingEmail: string
  collectionType?: 'home' | 'stationary'
  collectionPointId?: number
  collectionSlotId?: number
  collectionSlotDateFrom?: string
  selectedCollectionPoint?: {
    address: string
    postalCode: string
    city: string
    location: { coordinates: [number, number] }
  }
  isVoucher?: boolean
}

const NewBasket = () => {
  const i18n = useI18n()
  const { isLogged } = useContext(AuthContext)
  const isShowShippingTestsEnabled = useAtomValue(isShowShippingTestsEnabledAtom)
  const {
    isEmpty,
    tests,
    shippingTests,
    trainings,
    consultations,
    incompleteProfiles,
    removeItem,
  } = useBasketData()

  useEffect(() => {
    moment.locale(i18n.locale)
  }, [i18n.locale])

  useEffect(() => {
    if (!isShowShippingTestsEnabled && shippingTests.length) {
      removeItem(...shippingTests)
      addWarning(i18n.t('screens.basket.sections.shippingTests.removedWarning'))
    }
  }, [isShowShippingTestsEnabled, shippingTests, removeItem])

  const {
    isTestsSectionVisible,
    isConsultationsSectionVisible,
    isTrainingsSectionVisible,
    isShippingTestsSectionVisible,
  } = useMemo(
    () => ({
      isTestsSectionVisible: !!tests.length,
      isTrainingsSectionVisible: !!trainings.length,
      isConsultationsSectionVisible: !!consultations.length,
      isShippingTestsSectionVisible: !!shippingTests.length,
    }),
    [tests, trainings, consultations, shippingTests]
  )

  return (
    <Frame
      leftClassName="hidden lg:block lg:w-[100px] xl:w-[200px] 2xl:w-[300px]"
      rightClassName="hidden lg:block lg:w-[100px] xl:w-[200px] 2xl:w-[300px]"
    >
      <p className="text-blue-700 mb-5 xl:w-[300px]">
        <Link to="/">{i18n.t('common.breadcrumbs.mainPage')}</Link>
        {' > '}
        {i18n.t('screens.basket.breadcrumbs.basket')}
      </p>

      <h1 className="mb-5 mt-5 text-2xl md:text-3xl font-bold flex items-center gap-2.5">
        {i18n.t('screens.basket.title')}
      </h1>

      {!isEmpty ? (
        <div className="flex flex-col gap-2">
          {isTestsSectionVisible && <Tests />}
          {isShippingTestsSectionVisible && <ShippingTests />}
          {(isTestsSectionVisible || isShippingTestsSectionVisible) && (
            <MaterialPreparation />
          )}
          {isConsultationsSectionVisible && <Consultations />}
          {isTrainingsSectionVisible && <Trainings />}
          {isLogged && !!incompleteProfiles.length && <Profiles />}
          {isLogged && (
            <>
              <Discounts />
              <Consents />
            </>
          )}
          <Summary />
        </div>
      ) : (
        <span className="flex flex-col gap-2 justify-center items-center my-5 text-blue-700 text-center">
          <MdDoDisturb color={Red800} size={48} />
          {i18n.t('screens.basket.basket.empty')}
        </span>
      )}

      <SelectSlotModal />
      <SelectCollectionSlotModal />
      <MissingGlobalConsentsModal />
    </Frame>
  )
}

type BasketFormProviderProps = {
  children: JSX.Element
}

const BasketFormProvider = ({ children }: BasketFormProviderProps) => {
  const formMethods = useForm<BasketForm>({
    defaultValues: {
      collectionDate: '',
      clientNotes: '',
      consent1: false,
      consent3: false,
      consent4: false,
      shippingConsent1: false,
      shippingConsent2: false,
      collectionType: 'home',
      collectionPointId: undefined,
      collectionSlotId: undefined,
      selectedCollectionPoint: undefined,
      isVoucher: false,
    },
    shouldFocusError: false,
  })
  return <FormProvider {...formMethods}>{children}</FormProvider>
}

export default () => (
  <BasketFormProvider>
    <NewBasket />
  </BasketFormProvider>
)
